// ============================================================================
// logger.ts — ЕДИНЫЙ логгер клиента. Цель: ТОЧНАЯ диагностика любых ошибок.
//   - console: info/warn/error/debug (как раньше);
//   - ring-buffer: последние 300 записей в localStorage (`hydra_client_log`) —
//     durable, переживает перезагрузку; доступен из лаунчера ДО входа
//     (диагностика «Загрузка...» и т.п.);
//   - журнал регистрации (event_log, категория ui): warn/error пишутся
//     на сервер (онлайн) / в durable-очередь (офлайн) — видно на странице
//     «Журнал регистрации». info/debug — НЕ пишутся (спам).
// ПУСТЫЕ CATCH ЗАПРЕЩЕНЫ: каждая пойманная ошибка должна быть залогирована.
// ============================================================================

const LOG_KEY = 'hydra_client_log';
const LOG_LIMIT = 300;

interface LogEntry {
  ts: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  scope: string;
  message: string;
  details?: unknown;
}

function serialize(details?: unknown): string {
  try {
    return JSON.stringify(details ?? null);
  } catch {
    return String(details);
  }
}

export function readClientLog(limit = 200): LogEntry[] {
  try {
    const raw = localStorage.getItem(LOG_KEY);
    const list: LogEntry[] = raw ? JSON.parse(raw) : [];
    return list.slice(-limit);
  } catch {
    return [];
  }
}

function append(entry: LogEntry) {
  try {
    const raw = localStorage.getItem(LOG_KEY);
    const list: LogEntry[] = raw ? JSON.parse(raw) : [];
    list.push(entry);
    while (list.length > LOG_LIMIT) list.shift();
    localStorage.setItem(LOG_KEY, JSON.stringify(list));
  } catch { /* localStorage недоступен — лог только в консоль */ }
}

/**
 * Запись ТОЛЬКО в кольцевой буфер (`hydra_client_log`), БЕЗ журнала регистрации.
 * Для аварийной диагностики (watchdog зависания/автоперезагрузка): запись должна
 * пережить немедленную `location.reload()`, а отправка в журнал в этот момент
 * ненадёжна (успеет не всегда) — журнал получает событие при следующем старте.
 */
export function appendClientLog(
  level: LogEntry['level'],
  scope: string,
  message: string,
  details?: unknown,
): void {
  append({ ts: new Date().toISOString(), level, scope, message, details });
}

// Запись в журнал регистрации (сервер, категория ui) — с защитой от спама:
// не чаще 1 записи в секунду на один scope.
const lastJournalTs: Record<string, number> = {};
function toJournal(level: 'warn' | 'error', scope: string, message: string, details?: unknown) {
  const now = Date.now();
  if (now - (lastJournalTs[scope] || 0) < 1000) return;
  lastJournalTs[scope] = now;
  try {
    // Динамический импорт: eventLog загружается лениво (не ломает старт лаунчера)
    void import('./eventLog').then(({ eventLog }) => {
      const extra = details !== undefined ? { details: serialize(details) } : {};
      if (level === 'error') void eventLog.error('ui', message, { ...extra, entityType: scope });
      else void eventLog.warning('ui', message, { ...extra, entityType: scope });
    }).catch(() => { /* журнал недоступен (до входа) — буфер уже сохранил */ });
  } catch { /* ignore */ }
}

export interface HydraLogger {
  info: (message: string, details?: unknown) => void;
  warn: (message: string, details?: unknown) => void;
  error: (message: string, details?: unknown) => void;
  debug: (message: string, details?: unknown) => void;
}

export function createLogger(name: string): HydraLogger {
  const fmt = (details?: unknown) => (details !== undefined ? ` ${serialize(details)}` : '');
  const mk = (level: LogEntry['level']) => (message: string, details?: unknown) => {
    const entry: LogEntry = { ts: new Date().toISOString(), level, scope: name, message, details };
    append(entry);
    const line = `[${name}] ${message}${fmt(details)}`;
    switch (level) {
      case 'error': console.error(line); break;
      case 'warn': console.warn(line); break;
      case 'debug': console.debug(line); break;
      default: console.log(line);
    }
    if (level === 'warn' || level === 'error') toJournal(level, name, message, details);
  };
  return {
    info: mk('info'),
    warn: mk('warn'),
    error: mk('error'),
    debug: mk('debug'),
  };
}
